import { referenceDisplay } from '@seen/core/display'
import { Order } from '@seen/core/models/orders/Order'
import { Button, TypographyPageTitle } from '@seen/ui'
import { IconChevronDown, IconPlus } from '@tabler/icons-react'
import { isEmpty, orderBy } from 'lodash-es'
import { type JSX, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { ComboboxOption } from '#src/components/ComboboxOption.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { ShortcutSearchInput } from '#src/components/core/input/ShortcutSearchInput.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { Sheet } from '#src/components/core/sheet/Sheet.tsx'
import { SheetContent } from '#src/components/core/sheet/SheetContent.tsx'
import { OrderForm } from '#src/components/order/order-form/OrderForm.tsx'
import { OrderCreatedToast } from '#src/components/order/OrderCreatedToast.tsx'
import {
  type OrderListGrouping,
  OrdersList,
} from '#src/components/order/OrdersList.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useClientSideSearch } from '#src/hooks/useClientSideSearch.ts'
import { useDebounced } from '#src/hooks/useDebounced.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { toast } from '#src/utils/toast.ts'

export function PatientOrdersPage(): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const navigate = useNavigate()
  const location = useLocation()
  const isNewOrder = location.pathname.endsWith('/new')
  const participant = useCurrentParticipant()
  const [searchParams, setSearchParams] = useSearchParams()

  // Read and validate grouping from URL params, defaulting to 'status'
  const groupByParam = searchParams.get('groupBy')
  const grouping: OrderListGrouping =
    groupByParam === 'status' || groupByParam === 'performer'
      ? groupByParam
      : groupByParam === 'none'
        ? undefined
        : 'status'

  const updateGrouping = useCallback(
    (value: string | null) => {
      setSearchParams(
        (prev) => {
          if (!value) {
            prev.delete('groupBy')
          } else {
            prev.set('groupBy', value)
          }
          return prev
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const { data, error, isLoading } = useSearch(Order, {
    ...Order.byParticipant({ participant: participant.reference }),
    _sort: '-_lastUpdated',
  })

  const orders = useMemo(
    () =>
      orderBy(
        data,
        [
          (order) => order.dueDate ?? '9999-12-31', // Orders without due dates go last
        ],
        ['asc']
      ),
    [data]
  )

  /* Local input state decoupled from the URL so typing never blocks on
   * router updates or MiniSearch index rebuilds. The URL is driven off
   * the debounced value. Lazy initializer reads the URL param once on
   * mount; deep links and browser-history navigations work because React
   * Router mounts a fresh component instance on each navigation. */
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get('search') ?? ''
  )
  const debouncedSearch = useDebounced(searchInput, 300)

  /* Push the debounced input value to the URL. Skips when already in sync
   * to avoid unnecessary history writes. Preserves all other params (e.g.
   * groupBy). */
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const current = prev.get('search') ?? ''
        if (current === debouncedSearch) {
          return prev
        }
        if (debouncedSearch) {
          prev.set('search', debouncedSearch)
        } else {
          prev.delete('search')
        }
        return prev
      },
      { replace: true }
    )
  }, [debouncedSearch, setSearchParams])

  const extractOrderSearchFields = useCallback(
    (order: Order) => ({
      title: order.title ?? '',
      aiTitle: order.aiTitle ?? '',
      category: order.categoryCodeDisplay ?? '',
      details: order.orderDetailsText ?? '',
      reasons: order.reasonTexts.join(' '),
      specialty: order.specialty?.display ?? '',
      performer: referenceDisplay(order.performer) ?? '',
      requester: referenceDisplay(order.requester) ?? '',
      identifier: order.seenIdentifier ?? '',
      authorizationId: order.authorizationId ?? '',
      // Line-item test names (e.g. "Complete Metabolic Panel") from SR.code.coding[]
      codeDisplays: order.displayCodes
        .map((c) => c.display)
        .filter(Boolean)
        .join(' '),
    }),
    []
  )

  const { search: searchOrders, getExtractedFields } = useClientSideSearch(
    orders,
    {
      fields: [
        'title',
        'aiTitle',
        'category',
        'details',
        'reasons',
        'specialty',
        'performer',
        'requester',
        'identifier',
        'authorizationId',
        'codeDisplays',
      ],
      boost: {
        title: 5,
        aiTitle: 5,
        identifier: 4,
        authorizationId: 3,
        codeDisplays: 3,
        category: 2,
        performer: 2,
        requester: 2,
        specialty: 2,
        details: 1,
        reasons: 1,
      },
      extractFields: extractOrderSearchFields,
      idleBuild: true,
    }
  )

  const { items: displayedOrders, matchById } = useMemo(() => {
    const result = searchOrders(debouncedSearch)
    // Re-apply due-date sort after search so clinicians always see the most
    // time-sensitive orders first, even when MiniSearch re-ranks by relevance.
    if (debouncedSearch) {
      result.items = orderBy(
        result.items,
        [(o) => o.dueDate ?? '9999-12-31'],
        ['asc']
      )
    }
    return result
  }, [searchOrders, debouncedSearch])

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <TypographyPageTitle>Orders</TypographyPageTitle>
        <div className="flex items-center gap-2">
          <ShortcutSearchInput
            value={searchInput}
            onChange={setSearchInput}
            className="w-48 shrink-0 shadow-sm"
          />
          <Combobox
            options={[
              {
                label: (
                  <ComboboxOption isSelected={grouping === undefined}>
                    No grouping
                  </ComboboxOption>
                ),
                value: 'none',
                keywords: ['No grouping'],
              },
              {
                label: (
                  <ComboboxOption isSelected={grouping === 'status'}>
                    Group by status
                  </ComboboxOption>
                ),
                value: 'status',
                keywords: ['Group by status'],
              },
              {
                label: (
                  <ComboboxOption isSelected={grouping === 'performer'}>
                    Group by performer
                  </ComboboxOption>
                ),
                value: 'performer',
                keywords: ['Group by performer'],
              },
            ]}
            onSelect={updateGrouping}
          >
            <Button variant="outline" className="gap-2">
              {grouping === 'status' && 'Group by status'}
              {grouping === 'performer' && 'Group by performer'}
              {!grouping && 'No grouping'}
              <IconChevronDown className="size-4" />
            </Button>
          </Combobox>
          {!isReadOnly && (
            <Button
              variant="outline"
              onClick={() => navigate('new')}
              className="flex items-center gap-2"
            >
              <IconPlus size={16} />
              New Order
            </Button>
          )}
        </div>
      </div>
      {isLoading && <Loader />}
      {!isLoading && isEmpty(orders) && (
        <p className="text-muted-foreground">
          There are no orders for {participant.name}.
        </p>
      )}
      {!isLoading &&
        !isEmpty(orders) &&
        debouncedSearch &&
        isEmpty(displayedOrders) && (
          <p className="text-muted-foreground">
            No orders matched your search.
          </p>
        )}
      {!isLoading && !isEmpty(orders) && !isEmpty(displayedOrders) && (
        <div className="-mx-pagex-default lg:-mx-pagex-lg">
          <OrdersList
            patientId={participant.id}
            data={displayedOrders}
            isLoading={isLoading}
            error={error}
            grouping={grouping}
            matchById={debouncedSearch ? matchById : undefined}
            getExtractedFields={
              debouncedSearch ? getExtractedFields : undefined
            }
          />
        </div>
      )}
      <Sheet
        open={isNewOrder}
        onOpenChange={(open) => !open && navigate('..', { replace: true })}
      >
        <SheetContent hideClose side="right" className="max-w-2xl">
          <OrderForm
            onCancel={() => navigate('..')}
            onSubmit={(newOrder, options) => {
              void navigate('..')
              // Skip the generic "Order created" toast for deferred (async)
              // submissions — LabSubmissionWatcher surfaces the terminal
              // toast once the subscription handler completes.
              if (!options?.deferred) {
                toast.success(<OrderCreatedToast order={newOrder} />)
              }
            }}
          />
        </SheetContent>
      </Sheet>
    </div>
  )
}
